<template>
  <v-container class="tournaments-main">
    <v-row class="mb-6">
      <v-col cols="12" sm="8">
        <h1 class="text-h4 font-weight-bold primary--text">
          <v-icon large color="primary" class="mr-3">mdi-tournament</v-icon>
          Torneos de Golf
        </h1>
        <div class="grey--text text--darken-1">
          Consulta y participa en los torneos disponibles
        </div>
      </v-col>
      <v-col cols="12" sm="4" class="text-sm-right">
        <v-btn
          v-if="isRP"
          color="primary"
          rounded
          elevation="2"
          @click="showCreateDialog = true"
        >
          <v-icon left>mdi-plus</v-icon>
          Nuevo Torneo
        </v-btn>
      </v-col>
    </v-row>

    <!-- Tabs -->
    <v-tabs v-model="activeTab" background-color="transparent" color="primary" class="mb-6">
      <v-tab>
        <v-icon left>mdi-calendar-check</v-icon>
        Torneos Disponibles
      </v-tab>
      <v-tab>
        <v-badge
          color="red"
          :content="myRegistrations.length.toString()"
          :value="myRegistrations.length > 0"
          overlap
        >
          <v-icon left>mdi-clipboard-list</v-icon>
          Mis Inscripciones
        </v-badge>
      </v-tab>
      <v-tab v-if="isRP">
        <v-icon left>mdi-settings</v-icon>
        Gestión
      </v-tab>
    </v-tabs>

    <!-- Contenido de Tabs -->
    <v-tabs-items v-model="activeTab" class="transparent">
      <!-- Tab 1: Torneos Disponibles -->
      <v-tab-item>
        <TournamentsList
          :tournaments="tournaments"
          :loading="loading"
          :is-rp="isRP"
          @register="handleRegister"
          @view="handleViewDetail"
          @edit="handleEditTournament"
          @delete="handleDeleteTournament"
        />
      </v-tab-item>

      <!-- Tab 2: Mis Inscripciones -->
      <v-tab-item>
        <MyTournaments
          :registrations="myRegistrations"
          :tournaments="tournaments"
          :loading="loading"
          @cancel="handleCancelRegistration"
        />
      </v-tab-item>

      <!-- Tab 3: Gestión (Solo RP) -->
      <v-tab-item v-if="isRP">
        <TournamentsManagement
          :tournaments="tournaments"
          :loading="loading"
          @refresh="fetchTournaments()"
          @edit="handleEditTournament"
          @delete="handleDeleteTournament"
          @view-registrations="handleViewRegistrations"
        />
      </v-tab-item>
    </v-tabs-items>

    <!-- Dialog: Crear/Editar Torneo -->
    <CreateTournamentDialog
      v-if="showCreateDialog"
      :tournament="editingTournament"
      @create="handleCreateTournament"
      @update="handleUpdateTournament"
      @close="showCreateDialog = false"
    />

    <!-- Dialog: Ver Detalle -->
    <TournamentDetailDialog
      v-if="showDetailDialog"
      :tournament="selectedTournament"
      :loading="loading"
      :is-registered="isUserRegistered"
      @register="handleRegister"
      @close="showDetailDialog = false"
    />

    <!-- Dialog: Inscribirse -->
    <RegisterDialog
      v-if="showRegisterDialog"
      :tournament="selectedTournament"
      :loading="loading"
      :is-rp="isRP"
      @register="handleRegisterConfirm"
      @close="showRegisterDialog = false"
    />

    <!-- Dialog: Ver Inscritos (RP) -->
    <RegistrationsDialog
      v-if="showRegistrationsDialog"
      :tournament="selectedTournament"
      :registrations="registrations"
      :loading="loading"
      @close="showRegistrationsDialog = false"
      @register-player="handleRegisterPlayerByRP"
    />

    <!-- Snackbar: Mensajes -->
    <v-snackbar v-model="showSnackbar" :color="snackbarColor" top>
      {{ snackbarMessage }}
      <template #action="{ attrs }">
        <v-btn text v-bind="attrs" @click="showSnackbar = false">
          Cerrar
        </v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex'
import TournamentsList from './TournamentsList.vue'
import MyTournaments from './MyTournaments.vue'
import TournamentsManagement from './TournamentsManagement.vue'
import CreateTournamentDialog from './dialogs/CreateTournamentDialog.vue'
import TournamentDetailDialog from './dialogs/TournamentDetailDialog.vue'
import RegisterDialog from './dialogs/RegisterDialog.vue'
import RegistrationsDialog from './dialogs/RegistrationsDialog.vue'

export default {
  name: 'TournamentsMain',
  components: {
    TournamentsList,
    MyTournaments,
    TournamentsManagement,
    CreateTournamentDialog,
    TournamentDetailDialog,
    RegisterDialog,
    RegistrationsDialog
  },
  data () {
    return {
      activeTab: 0,
      showCreateDialog: false,
      showDetailDialog: false,
      showRegisterDialog: false,
      showRegistrationsDialog: false,
      showSnackbar: false,
      snackbarMessage: '',
      snackbarColor: 'info',
      selectedTournament: null,
      editingTournament: null,
      isUserRegistered: false
    }
  },
  computed: {
    ...mapState('tournaments', ['tournaments', 'myRegistrations', 'registrations', 'loading', 'error', 'success']),
    ...mapGetters(['getProfile']),
    isRP () {
      return this.getProfile?.permissions?.includes('golf_public_relations') || this.getProfile?.is_staff === true || this.getProfile?.is_superuser === true
    }
  },
  watch: {
    error (newVal) {
      if (newVal) {
        this.showSnackbarMessage(newVal, 'error')
      }
    },
    success (newVal) {
      if (newVal) {
        this.showSnackbarMessage(newVal, 'success')
        this.$store.dispatch('tournaments/clearMessages')
      }
    }
  },
  mounted () {
    this.loadData()
  },
  methods: {
    ...mapActions('tournaments', [
      'fetchTournaments',
      'fetchMyRegistrations',
      'registerInTournament',
      'updateTournament',
      'deleteTournament',
      'cancelRegistration',
      'createTournament',
      'fetchRegistrations'
    ]),
    async loadData () {
      await this.fetchTournaments()
      await this.fetchMyRegistrations()
    },
    handleRegister (tournament) {
      this.selectedTournament = tournament
      this.isUserRegistered = this.$store.getters['tournaments/isUserRegistered'](tournament.id)
      // Los RP siempre pueden abrir el diálogo, aunque ya estén inscritos,
      // porque pueden querer inscribir a alguien más.
      if (this.isUserRegistered && !this.isRP) {
        this.showSnackbarMessage('Ya estás inscrito en este torneo', 'warning')
      } else {
        this.showRegisterDialog = true
      }
    },
    async handleRegisterConfirm (data) {
      const isSelf = data.user && data.user === this.getProfile?.id
      const isRPRegistration = !isSelf && (data.user_id || data.ccslp_user_id || data.guest_name)
      try {
        if (isRPRegistration) {
          await this.$store.dispatch('tournaments/registerPlayerByRP', {
            tournamentId: this.selectedTournament.id,
            data
          })
        } else {
          await this.registerInTournament({
            tournamentId: this.selectedTournament.id,
            data
          })
        }
        this.showRegisterDialog = false
        if (isSelf) {
          this.activeTab = 1
          await this.fetchMyRegistrations()
        } else {
          await this.fetchTournaments()
        }
      } catch (err) {
        // Error manejado en el store
      }
    },
    handleViewDetail (tournament) {
      this.selectedTournament = tournament
      this.isUserRegistered = this.$store.getters['tournaments/isUserRegistered'](tournament.id)
      this.showDetailDialog = true
    },
    handleEditTournament (tournament) {
      this.editingTournament = tournament
      this.showCreateDialog = true
    },
    async handleUpdateTournament (data) {
      try {
        await this.updateTournament({
          id: this.editingTournament.id,
          data
        })
        this.showCreateDialog = false
        this.editingTournament = null
      } catch (err) {
        // Error manejado en el store
      }
    },
    async handleDeleteTournament (tournament) {
      if (confirm(`¿Estás seguro de eliminar el torneo "${tournament.name}"?`)) {
        try {
          await this.deleteTournament(tournament.id)
        } catch (err) {
          // Error manejado en el store
        }
      }
    },
    async handleCancelRegistration (registrationId) {
      if (confirm('¿Estás seguro de cancelar tu inscripción?')) {
        try {
          await this.cancelRegistration(registrationId)
          await this.fetchMyRegistrations()
        } catch (err) {
          // Error manejado en el store
        }
      }
    },
    async handleCreateTournament (data) {
      try {
        await this.createTournament(data)
        this.showCreateDialog = false
      } catch (err) {
        // Error manejado en el store
      }
    },
    async handleViewRegistrations (tournament) {
      this.selectedTournament = tournament
      try {
        await this.fetchRegistrations(tournament.id)
        this.showRegistrationsDialog = true
      } catch (err) {
        // Error manejado en el store
      }
    },
    async handleRegisterPlayerByRP ({ tournamentId, data }) {
      try {
        await this.$store.dispatch('tournaments/registerPlayerByRP', {
          tournamentId,
          data
        })
        this.showRegistrationsDialog = false
        await this.fetchRegistrations(tournamentId)
      } catch (err) {
        // Error manejado en el store
      }
    },
    showSnackbarMessage (message, color = 'info') {
      this.snackbarMessage = message
      this.snackbarColor = color
      this.showSnackbar = true
    }
  }
}
</script>

<style scoped>
.tournaments-main {
  padding: 20px;
}

.v-tabs {
  margin-bottom: 24px;
}

::v-deep .v-tabs-bar {
  border-bottom: 1px solid #e0e0e0;
}
</style>
